import { useEffect, useRef } from 'react';

interface AdInitializerProps {
  adElement: HTMLDivElement | null;
  adSlotId: string;
  adClient: string;
}

export const useAdInitializer = () => {
  const initializedAds = useRef<Set<string>>(new Set());

  const initializeAd = (adElement: HTMLDivElement | null, adSlotId: string, adClient: string) => {
    console.log('🎯 AdInitializer called with:', { adElement: !!adElement, adSlotId, adClient });
    
    if (!adElement) {
      console.log('❌ AdInitializer: No ad element provided');
      return;
    }

    // Create unique identifier for this ad
    const adId = `${adClient}-${adSlotId}`;
    
    // Check if this ad is already initialized
    if (initializedAds.current.has(adId)) {
      console.log('⚠️ AdInitializer: Ad already initialized:', adId);
      return;
    }

    // Find the ins element within the ad container
    const insElement = adElement.querySelector('.adsbygoogle') as HTMLElement;
    if (!insElement) {
      console.log('❌ AdInitializer: No .adsbygoogle element found');
      return;
    }

    // Check if this specific ins element already has ads
    const adStatus = insElement.getAttribute('data-adsbygoogle-status');
    if (adStatus) {
      console.log('⚠️ AdInitializer: Ad already has status:', adStatus);
      return;
    }

    // Mark as initialized
    initializedAds.current.add(adId);
    console.log('✅ AdInitializer: Marking ad as initialized:', adId);

    // Initialize the ad
    try {
      if (window.adsbygoogle) {
        console.log('🚀 AdInitializer: Pushing ad to AdSense');
        
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        console.log('✅ AdInitializer: Ad pushed successfully');
        
        // Function to update all links to open in new tab
        const updateLinks = () => {
          // Direct links in the ad container
          const links = adElement.querySelectorAll('a');
          links.forEach(link => {
            if (link.target !== '_blank') {
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              console.log('🔗 Set target=_blank on ad link');
            }
          });
          
          // Links in iframes (if accessible - same origin only)
          const iframes = adElement.querySelectorAll('iframe');
          iframes.forEach(iframe => {
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              if (iframeDoc) {
                const iframeLinks = iframeDoc.querySelectorAll('a');
                iframeLinks.forEach(link => {
                  if (link.target !== '_blank') {
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    console.log('🔗 Set target=_blank on iframe ad link');
                  }
                });
              }
            } catch (e) {
              // Cross-origin iframe - can't access, this is normal for ads
            }
          });
        };
        
        // Use MutationObserver to catch when ad content is loaded
        const observer = new MutationObserver(() => {
          updateLinks();
        });
        
        // Observe the entire ad container for any changes
        observer.observe(adElement, { 
          childList: true, 
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'href']
        });
        
        // Run immediately and after delays to catch dynamically loaded ads
        updateLinks();
        setTimeout(updateLinks, 1000);
        setTimeout(updateLinks, 3000);
      } else {
        console.log('❌ AdInitializer: window.adsbygoogle not available');
        initializedAds.current.delete(adId);
      }
    } catch (error) {
      console.log('❌ Ad initialization error:', error);
      // Remove from initialized set so it can be retried
      initializedAds.current.delete(adId);
    }
  };

  return { initializeAd };
};

export default useAdInitializer;
